import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  CircularProgress,
  Avatar
} from '@mui/material';
// UI
import FullscreenDialog from 'UI/FullscreenDialog';
import {
  debouncedSearchBooks,
  fetchWorkEnrichment,
  fetchEditionEnrichment,
  resolveOlGenreForBookForm,
  setOpenLibraryCoverSize,
  OL_COVER_SIZE,
} from 'services/openLibraryService';
import useClubContext from 'contexts/Club';
import useBooksContext from 'contexts/Books';
import CoverPickerDialog from './CoverPickerDialog';
import { bookCoverAvatarSx } from 'utils/bookCoverDisplay';

const AddBookForm = ({ open, onClose, onBookAdded, onBookDeleted, editingBook = null }) => {
  const { currentClub } = useClubContext();
  const { createBook, updateBook, deleteBook } = useBooksContext();
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    theme: [],
    genre: '',
    coverImage: '',
    fiction: false,
    description: '',
    suggesterNotes: '',
    externalApiId: null
  });

  // Update form data when editingBook changes
  React.useEffect(() => {
    if (editingBook) {
      // Clean up theme data - remove any escaped quotes or malformed entries
      let cleanTheme = [];
      if (Array.isArray(editingBook.theme)) {
        cleanTheme = editingBook.theme
          .map(t => typeof t === 'string' ? t.replace(/^["']|["']$/g, '').trim() : '')
          .filter(t => t && t.trim() !== '');
      } else if (editingBook.theme) {
        const cleaned = editingBook.theme.replace(/^["']|["']$/g, '').trim();
        if (cleaned) {
          cleanTheme = [cleaned];
        }
      }

      const newFormData = {
        title: editingBook.title || '',
        author: editingBook.author || '',
        theme: cleanTheme,
        genre: editingBook.genre || '',
        coverImage: editingBook.coverImage || '',
        fiction: Boolean(editingBook.fiction),
        description: editingBook.description || '',
        suggesterNotes: editingBook.suggesterNotes ?? editingBook.suggester_notes ?? '',
        externalApiId: editingBook.externalApiId ?? null
      };
      setFormData(newFormData);
      
      // Set selectedBook for Autocomplete when editing
      if (editingBook.title) {
        setSelectedBook({
          id: editingBook.externalApiId,
          title: editingBook.title,
          author: editingBook.author,
          coverImage: editingBook.coverImage,
          genre: editingBook.genre,
          description: editingBook.description
        });
      }
    } else {
      setFormData({
        title: '',
        author: '',
        theme: [],
        genre: '',
        coverImage: '',
        fiction: false,
        description: '',
        suggesterNotes: '',
        externalApiId: null
      });
      setSelectedBook(null);
    }
  }, [editingBook]);
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Autocomplete state
  const [bookSuggestions, setBookSuggestions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [editionEnrichPromptOpen, setEditionEnrichPromptOpen] = useState(false);
  const [pendingOlCover, setPendingOlCover] = useState(null);
  const [enrichApplying, setEnrichApplying] = useState(false);

  const themeOptions = Array.isArray(currentClub?.themes) && currentClub?.themes.length > 0
    ? currentClub.themes
    : ['Classy', 'Creative', 'Curious'];
  const themesEnabled = currentClub?.themesEnabled !== false;

  const genres = [
    'Personal Development',
    'Mindfulness & Spirituality',
    'Psychology & Decision Making',
    'Leadership & Productivity',
    'Philosophy & Resilience',
    'Business & Entrepreneurship',
    'Health & Wellness',
    'Relationships & Communication',
    'Creativity & Innovation',
    'History & Biography',
    'Science & Technology',
    'Literature & Fiction',
    'Education & Reference',
    'General'
  ];

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleThemeChange = (event, value) => {
    setFormData(prev => ({ ...prev, theme: value }));
    
    // Clear error when user starts selecting
    if (errors.theme) {
      setErrors(prev => ({ ...prev, theme: '' }));
    }
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  // Autocomplete handlers - search using both title and author fields
  const handleBookSearch = useCallback((title, author) => {
    const titleTrimmed = (title || '').trim();
    const authorTrimmed = (author || '').trim();
    
    // Search if we have at least 2 characters in title or author
    if (titleTrimmed.length >= 2 || authorTrimmed.length >= 2) {
      setAutocompleteLoading(true);
      debouncedSearchBooks(titleTrimmed, authorTrimmed, (results) => {
        setBookSuggestions(results);
        setAutocompleteLoading(false);
      });
    } else {
      setBookSuggestions([]);
      setAutocompleteLoading(false);
    }
  }, []);

  const handleBookSelect = (event, value) => {
    if (value) {
      if (typeof value === 'string') {
        setSelectedBook(null);
        setFormData(prev => ({
          ...prev,
          title: value,
          externalApiId: null
        }));
        return;
      }
      setSelectedBook(value);
      const olId = typeof value.id === 'string' && value.id ? value.id : null;
      const categories = value.categories || [];

      const genreFromOl = resolveOlGenreForBookForm(categories, value.genre);

      setFormData(prev => ({
        ...prev,
        title: value.title,
        author: value.author,
        coverImage: value.coverImage
          ? setOpenLibraryCoverSize(value.coverImage, OL_COVER_SIZE.L)
          : '',
        genre: genreFromOl || prev.genre,
        description: value.description || prev.description,
        externalApiId: olId,
      }));

      if (olId) {
        fetchWorkEnrichment(olId).then((en) => {
          if (!en) return;
          const genreResolved = resolveOlGenreForBookForm(en.subjects, en.genre);
          setFormData((prev) => ({
            ...prev,
            description: en.description || prev.description,
            genre: genreResolved || prev.genre,
          }));
        });
      }
    } else {
      setSelectedBook(null);
      setFormData(prev => ({ ...prev, externalApiId: null }));
    }
  };

  const handleTitleInputChange = (event, value, reason) => {
    if (reason === 'input') {
      setSelectedBook(null);
    }
    setFormData(prev => {
      handleBookSearch(value, prev.author);
      return { ...prev, title: value };
    });

    if (errors.title) {
      setErrors(prev => ({ ...prev, title: '' }));
    }
  };

  const handleAuthorChange = (event) => {
    const value = event.target.value;
    setFormData(prev => {
      // Search with both current title and author value
      handleBookSearch(prev.title, value);
      return { ...prev, author: value };
    });
    
    // Clear error when user starts typing
    if (errors.author) {
      setErrors(prev => ({ ...prev, author: '' }));
    }
  };

  const applyCoverOnly = (coverImage) => {
    const stored = coverImage
      ? setOpenLibraryCoverSize(coverImage, OL_COVER_SIZE.L)
      : coverImage;
    setFormData((prev) => ({ ...prev, coverImage: stored }));
    setSelectedBook((prev) => (prev ? { ...prev, coverImage: stored } : prev));
  };

  const handlePickOlCover = (opt) => {
    setPendingOlCover(opt);
    setEditionEnrichPromptOpen(true);
  };

  const handlePickCustomCover = (url) => {
    applyCoverOnly(url);
  };

  const handleEditionEnrichNo = () => {
    if (pendingOlCover) {
      applyCoverOnly(pendingOlCover.coverImage);
    }
    setEditionEnrichPromptOpen(false);
    setPendingOlCover(null);
  };

  const handleEditionEnrichYes = async () => {
    if (!pendingOlCover) return;
    const { coverImage, editionKey } = pendingOlCover;
    const coverStored = coverImage
      ? setOpenLibraryCoverSize(coverImage, OL_COVER_SIZE.L)
      : coverImage;
    const workKey = formData.externalApiId;
    setEnrichApplying(true);
    try {
      const en = await fetchEditionEnrichment(editionKey, workKey);
      setFormData((prev) => {
        if (!en) {
          return { ...prev, coverImage: coverStored };
        }
        const genreResolved = resolveOlGenreForBookForm(en.subjects, en.genre);
        return {
          ...prev,
          coverImage: coverStored,
          description: en.description || prev.description,
          genre: genreResolved || prev.genre,
        };
      });
      setSelectedBook((prev) => (prev ? { ...prev, coverImage: coverStored } : prev));
    } finally {
      setEnrichApplying(false);
      setEditionEnrichPromptOpen(false);
      setPendingOlCover(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }
    
    if (themesEnabled && (!formData.theme || formData.theme.length === 0)) {
      newErrors.theme = 'At least one theme is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      const bookData = {
        title: formData.title.trim(),
        author: formData.author.trim(),
        theme: formData.theme,
        genre: formData.genre || null,
        coverImage: formData.coverImage.trim() || null,
        fiction: formData.fiction || false, // Ensure it's always a boolean
        description: formData.description.trim() || null,
        suggesterNotes: formData.suggesterNotes.trim() || null,
        externalApiId: formData.externalApiId || null
      };

      if (!currentClub) {
        setSubmitError('No club selected');
        return;
      }

      if (editingBook) {
        // Update existing book
        const updatedBook = await updateBook(editingBook.id, bookData);
        if (onBookAdded) onBookAdded(updatedBook); // Call callback if exists, though context handles state
      } else {
        // Add new book
        const newBook = await createBook(bookData);
        if (onBookAdded) onBookAdded(newBook); // Call callback if exists
      }
      
      // Reset form
      setFormData({
        title: '',
        author: '',
        theme: [],
        genre: '',
        coverImage: '',
        fiction: false,
        description: '',
        suggesterNotes: '',
        externalApiId: null
      });
      setSelectedBook(null);
      setBookSuggestions([]);
      
      onClose();
    } catch (error) {
      console.error(error);
      setSubmitError('Failed to add book. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingBook || !editingBook.id) return;
    
    if (window.confirm(`Are you sure you want to delete "${editingBook.title}"? This action cannot be undone.`)) {
      if (!currentClub) {
        setSubmitError('No club selected');
        return;
      }

      setLoading(true);
      setSubmitError('');
      
      try {
        await deleteBook(editingBook.id);
        if (onBookDeleted) onBookDeleted(editingBook.id);
        onClose();
      } catch (error) {
        setSubmitError('Failed to delete book. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        author: '',
        theme: [],
        genre: '',
        coverImage: '',
        fiction: false,
        discussionDate: '',
        description: '',
        suggesterNotes: '',
        externalApiId: null
      });
      setSelectedBook(null);
      setBookSuggestions([]);
      setErrors({});
      setSubmitError('');
      setCoverPickerOpen(false);
      setEditionEnrichPromptOpen(false);
      setPendingOlCover(null);
      setEnrichApplying(false);
      onClose();
    }
  };

  return (
    <FullscreenDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Typography variant="h5" component="div">
          {editingBook ? 'Edit Book' : 'Add New Book'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {editingBook ? 'Update the book information' : 'Add a new book to the club\'s collection'}
        </Typography>
      </DialogTitle>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          // Avoid overflow:hidden here: WKWebView often won't delegate touch scroll to DialogContent.
        }}
      >
        <DialogContent sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={bookSuggestions}
              value={selectedBook != null ? selectedBook : formData.title}
              onInputChange={handleTitleInputChange}
              onChange={handleBookSelect}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, val) => {
                if (val == null || typeof val === 'string') return false;
                return option.id === val.id;
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.title || '';
              }}
              getOptionKey={(option) =>
                typeof option === 'string'
                  ? option
                  : option.id || `${option.title || ''}::${option.author || ''}::${option.coverImage || ''}`
              }
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {option.coverImage && (
                      <Avatar
                        src={setOpenLibraryCoverSize(option.coverImage, OL_COVER_SIZE.M)}
                        alt={option.title}
                        sx={bookCoverAvatarSx({ width: 32, height: 48, borderRadius: 1 })}
                        variant="rounded"
                      />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.author}
                        {option.publishedDate ? ` · ${option.publishedDate}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Title *"
                  error={!!errors.title}
                  helperText={errors.title}
                  disabled={loading}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {autocompleteLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              loading={autocompleteLoading}
              noOptionsText="No books found"
              loadingText="Searching books..."
            />

            <TextField
              label="Author *"
              value={formData.author}
              onChange={handleAuthorChange}
              error={!!errors.author}
              helperText={errors.author}
              fullWidth
              disabled={loading}
            />

            {themesEnabled && (
              <Autocomplete
                multiple
                options={themeOptions}
                value={formData.theme}
                onChange={handleThemeChange}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Theme *"
                    error={!!errors.theme}
                    helperText={errors.theme}
                  />
                )}
              />
            )}

            <Autocomplete
              freeSolo
              options={genres}
              value={formData.genre}
              onChange={(e, newValue) => {
                setFormData((prev) => ({ ...prev, genre: newValue ?? '' }));
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setFormData((prev) => ({ ...prev, genre: newInputValue }));
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Genre"
                  fullWidth
                  helperText="Filled from Open Library (genre tag, subjects, or preset match); edit or choose below"
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar
                variant="rounded"
                src={formData.coverImage || undefined}
                alt=""
                sx={bookCoverAvatarSx({ width: 80, height: 120, flexShrink: 0 })}
              >
                {!formData.coverImage && (
                  <Typography variant="caption" align="center" sx={{ px: 0.5 }}>
                    No cover
                  </Typography>
                )}
              </Avatar>
              <Box sx={{ flex: 1, pt: 0.5 }}>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  onClick={() => setCoverPickerOpen(true)}
                >
                  Choose a different cover
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Browse Open Library edition covers or paste a custom image URL.
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
              helperText="Optional: Book description or summary"
            />

            <TextField
              label="Suggester's notes"
              value={formData.suggesterNotes}
              onChange={handleChange('suggesterNotes')}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
              helperText="Optional: Why you're suggesting this book, context for the club, etc."
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.fiction}
                  onChange={handleCheckboxChange('fiction')}
                  disabled={loading}
                />
              }
              label="Fiction"
            />

          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          {editingBook && (
            <Button
              onClick={handleDelete}
              disabled={loading}
              variant="outlined"
              color="error"
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            {loading ? (editingBook ? 'Updating...' : 'Adding...') : (editingBook ? 'Update Book' : 'Add Book')}
          </Button>
        </DialogActions>
      </Box>

      <CoverPickerDialog
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        workKey={formData.externalApiId}
        currentCoverImageUrl={formData.coverImage}
        onPickOlCover={handlePickOlCover}
        onPickCustomCover={handlePickCustomCover}
      />

      <Dialog
        open={editionEnrichPromptOpen}
        onClose={() => !enrichApplying && handleEditionEnrichNo()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>This edition</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Update description and genre using metadata from this edition?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditionEnrichNo} disabled={enrichApplying}>
            No, cover only
          </Button>
          <Button
            variant="contained"
            onClick={handleEditionEnrichYes}
            disabled={enrichApplying}
          >
            {enrichApplying ? 'Applying…' : 'Yes, update'}
          </Button>
        </DialogActions>
      </Dialog>
    </FullscreenDialog>
  );
};

export default AddBookForm;
